import {
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";
import { useLocation } from "react-router-dom";
import { useChatStore, MAX_MESSAGES } from "@/stores/chat-store";
import { useAuthStore } from "@/stores/auth-store";
import { getChatAdapter } from "@/lib/api/chat-real";
import type {
  ChatMessage,
  ChatStep,
  ChatServiceAdapter,
  StepContent,
  StreamingChatResponse,
  SuggestedChip,
} from "@/lib/api/chat";
import {
  usePageContext,
  toContextPayload,
  type PageContext,
} from "@/hooks/use-page-context";
import { filterActions } from "@/lib/chat/action-filter";
import {
  generateSuggestions,
  hasContextChanged,
} from "@/lib/chat/suggestion-generator";
import { matchSuggestedChipsToApprovedChips } from "@/lib/chat/suggestion-validator";
import { filterVisibleSuggestedChips } from "@/lib/chat/visible-suggestions";
import { getCanonicalWalkthroughSteps } from "@/lib/chat/page-walkthroughs";
import { normalizeChatRoute } from "../lib/chat/navigation-paths";
import {
  buildChatPrompt,
  CHAT_RESPONSE_STYLE_RULES,
  filterPageTourChips,
  isExplicitPageTourRequest,
} from "@/lib/chat/response-ux";
import { playSound } from "@/lib/sounds";
import { useOnboarding } from "@/hooks/use-onboarding";
import { useMe } from "@/hooks/queries/use-me";
import { resolvePreferredHost } from "@spectree/shared";

// ---------------------------------------------------------------------------
// Error classification — gives users actionable error messages
// ---------------------------------------------------------------------------

function classifyError(err: unknown): string {
  const msg =
    err instanceof Error ? err.message.toLowerCase() : String(err).toLowerCase();

  if (msg.includes("fetch") || msg.includes("networkerror") || msg.includes("failed to fetch") || msg.includes("network"))
    return "Couldn't reach the server — check your internet connection and try again.";

  if (msg.includes("401") || msg.includes("unauthorized") || msg.includes("session expired"))
    return "Your session has expired. Please refresh the page to sign in again.";

  if (msg.includes("429") || msg.includes("rate") || msg.includes("too many"))
    return "You're sending messages too quickly. Wait a moment and try again.";

  if (msg.includes("timeout") || msg.includes("timed out") || msg.includes("aborted"))
    return "The request timed out. Try a shorter question or try again in a moment.";

  if (msg.includes("500") || msg.includes("internal server"))
    return "Something went wrong on our end. Try again in a moment.";

  return "Something went wrong. Please try again.";
}

// ---------------------------------------------------------------------------
// Context types
// ---------------------------------------------------------------------------

interface ChatServiceContextValue {
  /** Send a message and receive a full response */
  sendMessage: (text: string) => Promise<void>;
  /** Send a message and stream the response token-by-token */
  sendMessageStreaming: (text: string) => void;
  /** Retry the last failed message */
  retryLastMessage: () => void;
  /** Abort an in-flight streaming response */
  stopStreaming: () => void;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const ChatServiceContext = createContext<ChatServiceContextValue | null>(null);

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Access the chat service from any component inside `<ChatProvider>`.
 * Throws if used outside the provider.
 */
export function useChatService(): ChatServiceContextValue {
  const context = useContext(ChatServiceContext);
  if (!context) {
    throw new Error("useChatService must be used within a <ChatProvider>");
  }
  return context;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

let messageIdCounter = 0;

function nextUserMessageId(): string {
  messageIdCounter += 1;
  return `user-msg-${String(messageIdCounter)}`;
}

/** Debounce delay for context evaluation (ms) */
const CONTEXT_DEBOUNCE_MS = 300;

// ---------------------------------------------------------------------------
// Onboarding Scenario Detection
// ---------------------------------------------------------------------------

/**
 * Maps user intent patterns to terminal walkthrough scenario IDs.
 * Used for client-side quick-match so common onboarding queries
 * are handled instantly without an LLM round-trip.
 *
 * Returns undefined when no match is found (falls through to LLM).
 */
/**
 * Host-neutral explanation of where to paste a generated agent command. The
 * actual per-host command strings come from the host-aware `cli-guidance.ts`
 * builders + the host-aware terminal walkthrough; this canned Sprout prose is
 * kept host-neutral (A8/A8b) so the static onboarding patterns don't fork, while
 * the server-side SYSTEM_PROMPT (A14) answers free-text questions per host.
 */
const CLI_HOST_PROMPT_EXPLANATION =
  "Paste this into your CLI host's prompt (the GitHub Copilot CLI or Claude Code session) — not into a normal bash or zsh prompt.";

const ONBOARDING_PATTERNS: {
  patterns: string[];
  scenarioId: string;
  /** When true, prefer the verified terminal walkthrough over an LLM answer. */
  directResponse?: boolean;
  /** Intro text shown above the terminal walkthrough. */
  terminalIntro: string;
  /** Context steps shown before the CLI example */
  contextSteps: string[];
  /** One-line summary shown above the terminal card */
  cliIntro: string;
}[] = [
  {
    patterns: [
      "create epic request",
      "submit epic request",
      "propose epic",
      "how to request",
      "epic request cli",
      "spectree request",
    ],
    scenarioId: "create-epic-request",
    directResponse: true,
    terminalIntro: "Here's how creating an epic request works:",
    contextSteps: [
      "Open your CLI host (the GitHub Copilot CLI or Claude Code) in your editor or terminal.",
      CLI_HOST_PROMPT_EXPLANATION,
      "Epic Requests are proposals that your team can review, vote on, and approve before they become full epics.",
      "Once submitted, your request appears on the Epic Requests page where team members can react and comment.",
      "When approved by an admin, the request can be converted into a full epic with features and tasks.",
    ],
    cliIntro: "Paste this into your CLI host:",
  },
  {
    patterns: [
      "create epic",
      "new epic",
      "make an epic",
      "set up epic",
      "epic from cli",
      "spectree epic create",
    ],
    scenarioId: "create-epic",
    directResponse: true,
    terminalIntro: "Here's how creating an epic works:",
    contextSteps: [
      "Open your CLI host (the GitHub Copilot CLI or Claude Code) in your editor or terminal.",
      CLI_HOST_PROMPT_EXPLANATION,
      "Epics are the top-level containers in SpecTree — they hold features, which hold tasks.",
      "The planner agent will walk you through naming, describing, and structuring your epic with features and tasks automatically.",
      "After creation, your epic appears on the Epics page where you can manage features, track progress, and kick off orchestration.",
    ],
    cliIntro: "Paste this into your CLI host:",
  },
  {
    patterns: [
      "start orchestration",
      "run orchestration",
      "launch orchestrator",
      "ai orchestrate",
      "spectree orchestrate",
      "execute epic",
    ],
    scenarioId: "start-orchestration",
    directResponse: true,
    terminalIntro: "Here's how starting orchestration works:",
    contextSteps: [
      "Make sure your epic has an execution plan with features ordered by dependency and complexity.",
      "The orchestrator reads the plan, then spawns feature-worker agents to implement each feature phase by phase.",
      "Each phase is reviewed automatically before the next one starts — you can monitor progress in SpecTree.",
      "Open your CLI host (the GitHub Copilot CLI or Claude Code) in your editor or terminal to begin.",
      CLI_HOST_PROMPT_EXPLANATION,
    ],
    cliIntro: "Paste this into your CLI host:",
  },
  {
    patterns: [
      "setup",
      "set up",
      "getting started",
      "get started",
      "dev env",
      "development environment",
      "install spectree",
      "install cli",
      "configure copilot",
      "quick start",
      "quickstart",
      "how to install",
      "how to set up",
      "help me setup",
      "help me set up",
      "onboard",
      "cli setup",
      "setup cli",
    ],
    scenarioId: "getting-started",
    directResponse: true,
    terminalIntro: "Here's how to set up the SpecTree CLI:",
    contextSteps: [
      "Start in your normal terminal for the setup commands, not in your CLI host's chat.",
      "Make sure Node.js 20+, GitHub CLI (`gh`), and your CLI host (the GitHub Copilot CLI or Claude Code) are installed first.",
      "Run the one-time npm setup so your machine can download the SpecTree packages.",
      "After the install step, add your SpecTree API token to your CLI host's MCP config.",
      "Then ask your CLI host to list your epics to make sure everything is connected.",
    ],
    cliIntro: "I’ll show the setup commands in the order you should run them:",
  },
];

/** Normalize user input: lowercase, strip dashes/underscores/punctuation, collapse spaces */
function normalizeInput(text: string): string {
  return text
    .toLowerCase()
    .replace(/[-_/\\]/g, " ")
    .replace(/['".,!?;:()[\]{}]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function getMatchedOnboardingPattern(
  text: string,
): (typeof ONBOARDING_PATTERNS)[number] | undefined {
  const normalized = normalizeInput(text);

  let bestMatch:
    | { entry: (typeof ONBOARDING_PATTERNS)[number]; score: number }
    | undefined;

  for (const entry of ONBOARDING_PATTERNS) {
    for (const pattern of entry.patterns) {
      const normalizedPattern = normalizeInput(pattern);
      if (!normalized.includes(normalizedPattern)) continue;

      const score = normalizedPattern.length;
      if (!bestMatch || score > bestMatch.score) {
        bestMatch = { entry, score };
      }
    }
  }

  return bestMatch?.entry;
}


// ---------------------------------------------------------------------------
// Page-Aware Intent System
// ---------------------------------------------------------------------------
// Knows what actually exists on each page and provides grounded, accurate
// responses. This prevents the LLM from hallucinating non-existent buttons,
// forms, or screens. Runs BEFORE onboarding match and BEFORE LLM fallback.

type IntentEntry = {
  /** Word stems that trigger this intent (checked against normalized input) */
  triggers: string[];
  /** When true, prefer this verified response over an LLM-generated answer. */
  directResponse?: boolean;
  /** Return accurate response messages for this page + intent combo */
  respond: () => ChatMessage[];
};

/**
 * Page-level knowledge base.
 * Key = pathname (exact or prefix).
 * Each page has intent entries that know EXACTLY what exists on that page.
 */
const PAGE_INTENTS: Record<string, IntentEntry[]> = {
  "/epics": [
    {
      triggers: ["create", "new", "add", "make", "start"],
      directResponse: true,
      respond: () => {
        const ts = Date.now();
        return [
          {
            id: `page-intent-${String(ts)}`,
            role: "assistant",
            content: "Epics are created from your CLI host — there is no create button on this page. The planner takes your idea and turns it into features and tasks for you.",
            type: "steps",
            metadata: {
              type: "steps",
              steps: [
                { text: "Open your CLI host (the GitHub Copilot CLI or Claude Code) in your editor or terminal." },
                { text: "The planner will ask what you want to build, then turn that into a full epic with features, tasks, and execution order." },
                { text: "Once created, your new epic will appear here on this page — click it to manage features and start orchestration." },
              ],
            },
            timestamp: ts,
          },
          {
            id: `page-intent-terminal-${String(ts + 1)}`,
            role: "assistant",
            content: "Paste this into your CLI host:",
            type: "terminal",
            metadata: { type: "terminal", scenarioId: "create-epic" },
            timestamp: ts + 1,
          },
        ];
      },
    },
    {
      triggers: ["filter", "find", "search", "scope", "sort"],
      directResponse: true,
      respond: () => [{
        id: `page-intent-${String(Date.now())}`,
        role: "assistant",
        content: "Here's how to find what you need:",
        type: "steps",
        metadata: {
          type: "steps",
          steps: [
            { text: "Use the scope dropdown (top center) to filter between your epics, all epics, or epics by a specific team.", tourTargets: [{ target: "epics.cardGrid", description: "Your epics appear here as cards. Click any card to open it." }] },
            { text: "Click the Active/All filter to include or exclude archived epics.", highlightTarget: "epics.statusFilter" },
            { text: "Switch between card view and list view using the toggle buttons.", highlightTarget: "epics.viewToggle" },
          ],
        },
        timestamp: Date.now(),
      }],
    },
    {
      triggers: ["help", "what", "how", "guide", "show", "tour", "explain"],
      respond: () => [{
        id: `page-intent-${String(Date.now())}`,
        role: "assistant",
        content: "Here's what you can do on this page:",
        type: "steps",
        metadata: {
          type: "steps",
          steps: [
            {
              text: "Each card represents an epic — it shows the team badge, progress bar, feature count, and last edited date. Click any card to open its full detail view.",
              tourTargets: [
                { target: "epics.cardGrid", description: "Epic cards — click any card to open the epic and see its features, tasks, and execution plan." },
                { target: "epics.statusFilter", description: "Filter — toggle between Active epics and including Archived ones." },
                { target: "epics.viewToggle", description: "View toggle — switch between card view and compact list view." },
              ],
            },
            { text: "Use the scope dropdown to filter between your epics, all epics, or by a specific team or user." },
            { text: "To create a new epic, use the Planner agent in your terminal. To submit a proposal for team review, use the Request Formulator agent." },
          ],
        },
        timestamp: Date.now(),
      }],
    },
  ],
  "/epic-requests": [
    {
      triggers: ["create", "new", "add", "make", "submit", "propose"],
      directResponse: true,
      respond: () => {
        const ts = Date.now();
        return [
          {
            id: `page-intent-${String(ts)}`,
            role: "assistant",
            content: "There isn't a form on this page — `New Request` opens a helper dialog that shows the CLI command you should run.",
            type: "steps",
            metadata: {
              type: "steps",
              steps: [
                { text: "Click the `New Request` button in the top right corner of this page.", highlightTarget: "epicRequests.newRequest" },
                { text: "A helper dialog opens — it does not show a web form. It shows the exact CLI command to copy and tells you where to paste it." },
                { text: "Paste that command into your CLI host, answer the request-formulator questions, then come back here to see the new request in the list." },
              ],
            },
            timestamp: ts,
          },
          {
            id: `page-intent-terminal-${String(ts + 1)}`,
            role: "assistant",
            content: "Or run this directly in your terminal:",
            type: "terminal",
            metadata: { type: "terminal", scenarioId: "create-epic-request" },
            timestamp: ts + 1,
          },
        ];
      },
    },
    {
      triggers: ["filter", "find", "search", "sort", "status"],
      directResponse: true,
      respond: () => [{
        id: `page-intent-${String(Date.now())}`,
        role: "assistant",
        content: "Here's how to filter requests:",
        type: "steps",
        metadata: {
          type: "steps",
          steps: [
            { text: "Use the search bar to filter requests by title.", highlightTarget: "epicRequests.search" },
            { text: "Use the status dropdown to show or hide requests by status — Pending, Approved, Rejected, or Converted.", highlightTarget: "epicRequests.statusFilter" },
            { text: "Use the scope dropdown to switch between your requests, all requests, or by team." },
          ],
        },
        timestamp: Date.now(),
      }],
    },
    {
      triggers: ["help", "what", "how", "guide", "show", "tour", "explain"],
      respond: () => [{
        id: `page-intent-${String(Date.now())}`,
        role: "assistant",
        content: "Here's what you can do on this page:",
        type: "steps",
        metadata: {
          type: "steps",
          steps: [
            {
              text: "Each card shows a proposal from a team member — including the title, description preview, author, and submission time. Click any card to read the full proposal.",
              tourTargets: [
                { target: "epicRequests.newRequest", description: "New Request — opens a guide showing how to submit a new epic request with your CLI host." },
                { target: "epicRequests.search", description: "Search — filter requests by title to find specific proposals." },
                { target: "epicRequests.statusFilter", description: "Status filter — show/hide requests by status: Pending, Approved, Rejected, or Converted." },
              ],
            },
            { text: "To submit a new request, click `New Request` and copy the CLI command it shows you.", highlightTarget: "epicRequests.newRequest" },
            { text: "You can approve your own requests to convert them into full epics, or reject them with feedback." },
          ],
        },
        timestamp: Date.now(),
      }],
    },
  ],
  "/teams": [
    {
      triggers: ["create", "new", "add", "make"],
      directResponse: true,
      respond: () => [{
        id: `page-intent-${String(Date.now())}`,
        role: "assistant",
        content: "Use the team hierarchy sidebar to create new teams — global admins can click the + button on a product team to add a discipline.",
        type: "steps",
        metadata: {
          type: "steps",
          steps: [
            { text: "Find the parent product team in the hierarchy sidebar on the left." },
            { text: "Global admins can click the **+** button on a product team to add a discipline." },
            { text: "A dialog opens. Type the team name first — the team key auto-fills from the name." },
          ],
        },
        timestamp: Date.now(),
      }],
    },
    {
      triggers: ["help", "what", "how", "guide", "show", "tour", "explain"],
      respond: () => [{
        id: `page-intent-${String(Date.now())}`,
        role: "assistant",
        content: "Here's what you can do on this page:",
        type: "steps",
        metadata: {
          type: "steps",
          steps: [
            {
              text: "Each team card shows the team name, key, and member count. Click a team to open its members and workflow settings.",
              tourTargets: [
                { target: "teams.title", description: "Teams — this page is where you browse and manage teams in your workspace." },
                { target: "teams.cardGrid", description: "Team cards — each card represents a team. Click one to open its members and workflow settings." },
              ],
            },
            { text: "To create a new team, global admins can click the **+** button on a product team in the sidebar to add a discipline." },
          ],
        },
        timestamp: Date.now(),
      }],
    },
  ],
  "/": [
    {
      triggers: ["help", "what", "how", "guide", "show", "tour", "explain", "metric", "chart", "data"],
      respond: () => [{
        id: `page-intent-${String(Date.now())}`,
        role: "assistant",
        content: "Here's what your dashboard shows:",
        type: "steps",
        metadata: {
          type: "steps",
          steps: [
            {
              text: "The dashboard shows key metrics — features created, tasks completed, decisions logged, and AI sessions for the selected period.",
              tourTargets: [
                { target: "dashboard.activityChart", description: "Activity chart — shows features, tasks, decisions, and sessions over time." },
                { target: "dashboard.metrics.features", description: "Features Created — total features your team created this period." },
                { target: "dashboard.metrics.tasks", description: "Tasks Completed — completed tasks this period." },
                { target: "dashboard.metrics.decisions", description: "Decisions Logged — architectural and design decisions recorded by AI agents." },
                { target: "dashboard.metrics.sessions", description: "AI Sessions — how many AI agent sessions ran on your epics." },
              ],
            },
            { text: "Use the interval selector (Weekly / Monthly) to change the time range for all charts and metrics.", highlightTarget: "dashboard.intervalSelector" },
            { text: "Click any metric card to see the full detail list for that metric." },
          ],
        },
        timestamp: Date.now(),
      }],
    },
  ],
};

/** Topics that should skip page-specific matching (handled by onboarding or LLM) */
const OFF_PAGE_TOPICS = [
  "install", "setup", "set up", "cli", "copilot", "claude", "terminal",
  "agent", "planner", "orchestrator", "request formulator",
  "command", "configure", "config", "api", "token", "auth",
  "login", "sign in", "signup", "sign up", "account",
  "spectree", "pricing", "plan", "subscription", "billing",
  "integrate", "integration", "webhook", "plugin",
];

// ---------------------------------------------------------------------------
// Grounding Context Extraction
// ---------------------------------------------------------------------------
// These functions expose the same verified page knowledge in two ways:
// - direct response for high-confidence page tasks
// - grounding context for all other matched page questions
// ---------------------------------------------------------------------------

function getMatchedPageIntent(
  text: string,
  pathname: string,
): { intent: IntentEntry; path: string } | undefined {
  const normalized = normalizeInput(text);

  if (OFF_PAGE_TOPICS.some((t) => normalized.includes(t))) return undefined;

  const path = normalizeChatRoute(pathname);
  const intents = PAGE_INTENTS[path];
  if (!intents) return undefined;

  for (const intent of intents) {
    if (intent.triggers.some((t) => normalized.includes(t))) {
      return { intent, path };
    }
  }

  return undefined;
}

export function getVerifiedPageIntentResponse(
  text: string,
  pathname: string,
): ChatMessage[] | undefined {
  const match = getMatchedPageIntent(text, pathname);
  if (!match) return undefined;

  if (match.intent.directResponse || isExplicitPageTourRequest(text)) {
    return match.intent.respond();
  }

  return undefined;
}

/** Extract knowledge from onboarding patterns as plain text */
function extractOnboardingContext(text: string): string | undefined {
  const entry = getMatchedOnboardingPattern(text);
  if (!entry) return undefined;

  const steps = entry.contextSteps
    .map((s, i) => `${String(i + 1)}. ${s}`)
    .join("\n");
  return `[SpecTree Workflow: ${entry.scenarioId}]\n${entry.cliIntro}\n${steps}`;
}

export function getVerifiedOnboardingResponse(
  text: string,
): ChatMessage[] | undefined {
  const entry = getMatchedOnboardingPattern(text);
  if (!entry?.directResponse) return undefined;

  const timestamp = Date.now();
  return [
    {
      id: `onboarding-terminal-${String(timestamp)}`,
      role: "assistant",
      content: entry.terminalIntro,
      type: "terminal",
      metadata: {
        type: "terminal",
        scenarioId: entry.scenarioId,
      },
      timestamp,
    },
  ];
}

export function getVerifiedDirectResponse(
  text: string,
  pathname: string,
): ChatMessage[] | undefined {
  return (
    getVerifiedPageIntentResponse(text, pathname) ??
    getVerifiedOnboardingResponse(text)
  );
}

/** Extract knowledge from page-aware intents as plain text */
function extractPageContext(
  text: string,
  pathname: string,
): string | undefined {
  const match = getMatchedPageIntent(text, pathname);
  if (!match) return undefined;

  const msgs = match.intent.respond();
  const textParts = msgs.map((m) => {
    if (
      m.metadata &&
      typeof m.metadata === "object" &&
      "type" in m.metadata &&
      m.metadata.type === "steps" &&
      "steps" in m.metadata &&
      Array.isArray(m.metadata.steps)
    ) {
      const steps = (m.metadata.steps as Array<{ text: string }>)
        .map((s, i) => `${String(i + 1)}. ${s.text}`)
        .join("\n");
      return `${m.content}\n${steps}`;
    }
    return m.content;
  });
  return `[Page context: ${match.path}]\n${textParts.join("\n")}`;
}

/**
 * Extract grounding context from client-side knowledge base.
 * Checks page-aware intents and onboarding patterns, returning
 * matched knowledge as plain text for LLM prompt injection.
 * Returns undefined when no knowledge matches the query.
 */
export function extractGroundingContext(
  text: string,
  pathname: string,
): string | undefined {
  const parts: string[] = [];

  const pageCtx = extractPageContext(text, pathname);
  if (pageCtx) parts.push(pageCtx);

  const onboardingCtx = extractOnboardingContext(text);
  if (onboardingCtx) parts.push(onboardingCtx);

  if (parts.length === 0) return undefined;

  return parts.join("\n\n");
}

// ---------------------------------------------------------------------------
// Page step definitions & tour target map
// ---------------------------------------------------------------------------
// These define page-specific guidance steps and tour targets used by the
// navigation handler. They are at module level so both the context extraction
// functions and the navigation useEffect can reference them.

/** Step type re-exported from ChatStep for page guidance definitions */
type GuidedStep = ChatStep;

const PAGE_STEPS: Record<string, GuidedStep[]> = {
  "/": [
    {
      text: "Your dashboard shows key metrics at a glance — features created, tasks completed, decisions logged, and AI sessions this period.",
      tourTargets: [
        { target: "dashboard.activityChart", description: "Activity chart — shows features, tasks, decisions, and sessions over time. Hover bars for exact counts." },
        { target: "dashboard.metrics.features", description: "Features Created — total features your team created this period, with growth trend." },
        { target: "dashboard.metrics.tasks", description: "Tasks Completed — completed tasks this period. Click for a detailed list." },
        { target: "dashboard.metrics.decisions", description: "Decisions Logged — architectural and design decisions recorded by AI agents." },
        { target: "dashboard.metrics.sessions", description: "AI Sessions — how many AI agent sessions ran on your epics." },
        { target: "dashboard.whatsNew", description: "What\'s New — release notes and platform updates." },
        { target: "dashboard.activityBreakdown", description: "Activity Breakdown — period-by-period table of all activity metrics." },
      ],
    },
    { text: "Use the interval selector (Weekly / Monthly) to change the time range for all charts and metrics.", highlightTarget: "dashboard.intervalSelector" },
    { text: "Click any metric card with data to open a detail sheet with the full list of items." },
  ],
  "/epics": [
    {
      text: "Each card represents an epic — it shows the team badge, progress bar, feature count, and last edited date. Click any card to open its full detail view with features and tasks.",
      tourTargets: [
        { target: "epics.cardGrid", description: "Epic cards — each card shows an epic\'s name, description, team, and progress. Click to open." },
        { target: "epics.statusFilter", description: "Filter — toggle between Active epics and including Archived ones." },
        { target: "epics.viewToggle", description: "View toggle — switch between card view and compact list view." },
      ],
    },
    { text: "Use the scope dropdown to filter between your epics, all epics, or epics by a specific team or user." },
    { text: "To create a new epic, use the Planner agent in your terminal — it will structure your requirements into features and tasks automatically." },
  ],
  "/teams": [
    {
      text: "Each team card shows the team name, key (e.g. ENG), and member count. Click a team to manage its members, workflow statuses, and settings.",
      tourTargets: [
        {
          target: "teams.title",
          description: "Teams — this page is where you browse and manage teams in your workspace.",
        },
        {
          target: "teams.cardGrid",
          description: "Team cards — each card represents a team. Click one to open its members and workflow settings.",
        },
      ],
    },
    { text: "To create a new team, global admins can click the **+** button on a product team in the sidebar to add a discipline." },
    { text: "The team key is used as a prefix for all feature identifiers — so ENG team gets features like ENG-1, ENG-2, etc." },
    { text: "Team workflow statuses (Backlog, In Progress, Done, etc.) can be customised per team from the team settings page." },
  ],
  "/epic-requests": [
    {
      text: "Each card shows a proposal from a team member — including the title, description preview, author, and submission time. Click any card to read the full proposal and leave your vote or comments.",
      tourTargets: [
        { target: "epicRequests.newRequest", description: "New Request — opens a guide showing how to submit a new epic request with your CLI host." },
        { target: "epicRequests.search", description: "Search — filter requests by title to find specific proposals." },
        { target: "epicRequests.statusFilter", description: "Status filter — show/hide requests by status: Pending, Approved, Rejected, or Converted." },
      ],
    },
    { text: "To submit a new request, click `New Request` and copy the CLI command it shows you. The request-formulator then guides you through writing the proposal.", highlightTarget: "epicRequests.newRequest" },
    { text: "You can approve your own requests to convert them into full epics, or reject them with feedback." },
  ],
};

const DETAIL_STEPS: Record<string, GuidedStep[]> = {
  "epic-detail": [
    { text: "The description section contains the epic\'s purpose, goals, and technical approach. Expand it for full context.", highlightTarget: "epic.description.toggle" },
    { text: "The feature list shows every feature in this epic with status badges and assignees. Click any feature to see its tasks.", highlightTarget: "epic.featureList" },
    { text: "Switch to the Plan tab to see the execution order — features are grouped by phase and dependency.", highlightTarget: "epic.tabs.plan" },
    { text: "Use the Execute button to start AI orchestration, which will implement features phase by phase.", highlightTarget: "epic.execute.command" },
  ],
  "feature-detail": [
    { text: "The properties panel shows status, assignee, complexity, and execution order. Update these to track progress.", highlightTarget: "feature.properties.status" },
    { text: "The task list breaks this feature into actionable work items. Each task has its own status and can be assigned independently.", highlightTarget: "feature.taskList" },
    { text: "Check the structured description for AI instructions, acceptance criteria, and files involved." },
  ],
};

/** Convert step definitions to plain text for LLM grounding */
function getPageKnowledge(pathname: string): string | undefined {
  const path = normalizeChatRoute(pathname);

  let steps: GuidedStep[] | undefined;
  let pageLabel: string;

  if (PAGE_STEPS[path]) {
    steps = PAGE_STEPS[path];
    pageLabel =
      path.replace(/^\//, "").replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) ||
      "Dashboard";
  } else if (/^\/epics\/[^/]+\/?$/.test(path)) {
    steps = DETAIL_STEPS["epic-detail"];
    pageLabel = "Epic Detail";
  } else if (/^\/features\/[^/]+\/?$/.test(path)) {
    steps = DETAIL_STEPS["feature-detail"];
    pageLabel = "Feature Detail";
  } else {
    return undefined;
  }

  if (!steps || steps.length === 0) return undefined;

  const stepText = steps.map((s, i) => `${String(i + 1)}. ${s.text}`).join("\n");
  return `[Page: ${pageLabel}]\nKey features and actions on this page:\n${stepText}`;
}

/** Post-process an LLM response to add tour targets when it contains numbered steps */
function enhanceMessageWithTours(
  message: ChatMessage,
  pathname: string,
): ChatMessage {
  const walkthroughTargets = getCanonicalWalkthroughSteps(pathname);
  if (walkthroughTargets.length === 0) return message;

  // Only enhance text messages
  if (message.type !== "text") return message;

  // Parse numbered steps from LLM response
  const lines = message.content.split("\n");
  const itemStarts: { lineIdx: number; num: number }[] = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (!line) continue;
    const m = line.match(/^\s*(?:\*\*)?(\d+)[.)]\*?\*?\s+/);
    if (m?.[1]) itemStarts.push({ lineIdx: i, num: parseInt(m[1]) });
  }

  // Find longest consecutive run starting at 1
  let bestRun: typeof itemStarts = [];
  let run: typeof itemStarts = [];
  for (const item of itemStarts) {
    if (run.length === 0 && item.num === 1) {
      run = [item];
    } else if (run.length > 0 && item.num === run.length + 1) {
      run.push(item);
    } else if (item.num === 1) {
      if (run.length > bestRun.length) bestRun = run;
      run = [item];
    }
  }
  if (run.length > bestRun.length) bestRun = run;

  if (bestRun.length < 2) return message;

  const first = bestRun[0];
  if (!first) return message;
  const intro = lines.slice(0, first.lineIdx).join("\n").trim();

  const steps: GuidedStep[] = [];
  for (let i = 0; i < bestRun.length; i++) {
    const cur = bestRun[i];
    const next = bestRun[i + 1];
    if (!cur) continue;
    const end = next ? next.lineIdx : lines.length;
    const raw = lines.slice(cur.lineIdx, end).join("\n");
    const cleaned = raw.replace(/^\s*(?:\*\*)?(\d+)[.)]\*?\*?\s+/, "").trim();
    steps.push({ text: cleaned });
  }

  // Attach tour targets to the first step
  if (steps.length > 0 && steps[0]) {
    steps[0] = { ...steps[0], tourTargets: walkthroughTargets };
  }

  return {
    ...message,
    content: intro || message.content.split("\n")[0] || "Here\'s what you can do:",
    type: "steps",
    metadata: { type: "steps", steps, presentation: "tour" },
  };
}


// ---------------------------------------------------------------------------
// Contextual Follow-Up Chips
// ---------------------------------------------------------------------------
// After every assistant response, generate 2-3 hyper-relevant follow-up chips
// based on: what was just shown, the current page, and what hasn't been asked.

/** Page-specific follow-ups keyed by pathname */
const PAGE_FOLLOW_UPS: Record<string, SuggestedChip[]> = {
  "/": [
    { label: "Walk me through the dashboard", value: "Give me a tour of everything on this page" },
    { label: "What do the metrics mean?", value: "Explain what each metric card on the dashboard represents" },
    { label: "How do I change the time range?", value: "Show me how to change the interval for the dashboard charts" },
    { label: "Where are my epics?", value: "Take me to my epics so I can see my work" },
    { label: "How do I get started?", value: "What's the first thing I should do as a new user on SpecTree?" },
  ],
  "/epics": [
    { label: "How do I create an epic?", value: "Walk me through creating a new epic from your CLI host." },
    { label: "Walk me through this page", value: "Give me a tour of everything on this page" },
    { label: "How do I filter epics?", value: "Show me how to filter and find specific epics" },
    { label: "What does the progress bar show?", value: "Explain what the progress bar on each epic card means" },
    { label: "Open an epic", value: "How do I open an epic to see its features and tasks?" },
    { label: "Submit a proposal instead", value: "Take me to epic requests to submit a proposal" },
  ],
  "/epic-requests": [
    { label: "Submit a new request", value: "How do I create a new epic request?" },
    { label: "Walk me through this page", value: "Give me a tour of everything on this page" },
    { label: "Filter by status", value: "Show me how to filter requests by their approval status" },
    { label: "What happens after approval?", value: "What happens to an epic request after it gets approved?" },
    { label: "Go to my epics", value: "Take me to my epics" },
  ],
  "/teams": [
    { label: "Walk me through this page", value: "Give me a tour of everything on this page" },
    { label: "How do teams work?", value: "Explain how teams are organized in SpecTree" },
    { label: "Go to my epics", value: "Take me to my epics" },
  ],
};

/** Follow-ups based on what type of content was just shown */
const CONTENT_FOLLOW_UPS: Record<string, SuggestedChip[]> = {
  terminal: [
    { label: "Which command do I use?", value: "Which CLI command should I use for this workflow?" },
    { label: "How do I set this up?", value: "Walk me through the one-time setup for using SpecTree with your CLI host." },
  ],
  tour: [
    { label: "What else can I do here?", value: "What other actions are available on this page?" },
  ],
  navigation: [
    { label: "What can I do here?", value: "Explain what I can do on this page and where to start" },
  ],
  text: [
    { label: "Tell me more", value: "Can you expand on that with more detail?" },
    { label: "What should I do next?", value: "Based on what you just told me, what should I do next?" },
  ],
};

/** Fallback chips used when no content-type or page-specific chips match */
const GENERAL_FOLLOW_UPS: SuggestedChip[] = [
  { label: "Walk me through this page", value: "Give me a tour of everything on this page" },
  { label: "How do I use SpecTree?", value: "What's the first thing I should do as a new user on SpecTree?" },
  { label: "How do the CLI commands work?", value: "Explain which CLI commands I should use for the main SpecTree workflows." },
  { label: "Where are my epics?", value: "Take me to my epics so I can see my work" },
];

/**
 * Generate 2-3 contextual follow-up chips based on the last response + page.
 * Returns chips that are relevant and non-repetitive.
 */
function generateFollowUpChips(
  recentMessages: ChatMessage[],
  pathname: string,
): SuggestedChip[] {
  const path = normalizeChatRoute(pathname);

  // Find the last few assistant messages to understand what was just discussed
  const assistantMsgs = recentMessages
    .filter((m) => m.role === "assistant" && !m.id.startsWith("error-"))
    .slice(-3);
  const userMsgs = recentMessages.filter((m) => m.role === "user");

  if (assistantMsgs.length === 0) return [];

  // Safe because we checked length > 0 above
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const lastMsg = assistantMsgs[assistantMsgs.length - 1]!;
  const lastUserMsg = userMsgs[userMsgs.length - 1];
  const shouldOfferPageTour =
    (typeof lastUserMsg?.content === "string" &&
      isExplicitPageTourRequest(lastUserMsg.content)) ||
    lastMsg.type === "navigation" ||
    (lastMsg.type === "steps" &&
      lastMsg.metadata?.type === "steps" &&
      (lastMsg.metadata as StepContent).presentation === "tour");

  // Collect all user messages to avoid suggesting what was already asked
  const askedTopics = new Set(
    userMsgs.map((m) => normalizeInput(m.content))
  );

  const chips: SuggestedChip[] = [];

  // 1. Content-type specific follow-ups
  if (lastMsg.type === "terminal" || assistantMsgs.some((m) => m.type === "terminal")) {
    chips.push(...(CONTENT_FOLLOW_UPS.terminal ?? []));
  } else if (lastMsg.type === "navigation") {
    chips.push(...(CONTENT_FOLLOW_UPS.navigation ?? []));
  } else if (
    lastMsg.type === "steps" &&
    lastMsg.metadata?.type === "steps" &&
    (lastMsg.metadata as StepContent).presentation === "tour"
  ) {
    chips.push(...(CONTENT_FOLLOW_UPS.tour ?? []));
  } else {
    // Regular text response — add contextual follow-ups
    chips.push(...(CONTENT_FOLLOW_UPS.text ?? []));
  }

  // 2. If last message had steps, suggest drilling into specific steps
  if (lastMsg.type === "steps" && lastMsg.metadata?.type === "steps") {
    const steps = (lastMsg.metadata as { steps: Array<{ text: string }> }).steps;
    if (steps.length >= 2) {
      const stepIdx = Math.min(1, steps.length - 1);
      const step = steps[stepIdx];
      const stepPreview = step ? step.text.slice(0, 40) : "";
      chips.push({
        label: `Tell me more about step ${String(stepIdx + 1)}`,
        value: `Expand on step ${String(stepIdx + 1)}: "${stepPreview}..."`,
      });
    }
  }

  // 3. Page-specific follow-ups
  const pageChips = filterPageTourChips(
    PAGE_FOLLOW_UPS[path] ?? [],
    shouldOfferPageTour,
  );
  chips.push(...pageChips);

  // 4. Deduplicate and filter out already-asked topics
  const seen = new Set<string>();
  const filtered = chips.filter((chip) => {
    const key = chip.label.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    // Filter out chips whose value closely matches something already asked
    const normalizedValue = normalizeInput(chip.value);
    for (const asked of askedTopics) {
      // If >60% of words overlap, skip this chip
      const chipWords = normalizedValue.split(/\s+/);
      const askWords = asked.split(/\s+/);
      const overlap = chipWords.filter((w) => askWords.includes(w)).length;
      if (overlap > chipWords.length * 0.6) return false;
    }
    return true;
  });

  // Return at most 3 chips — guarantee at least 2 by pulling from general pool
  if (filtered.length < 2) {
    const generalPool = filterPageTourChips(GENERAL_FOLLOW_UPS, shouldOfferPageTour);
    const generalSeen = new Set(filtered.map((c) => c.label.toLowerCase()));
    for (const chip of generalPool) {
      if (filtered.length >= 3) break;
      const key = chip.label.toLowerCase();
      if (generalSeen.has(key)) continue;
      generalSeen.add(key);
      // Skip if already asked
      const nv = normalizeInput(chip.value);
      let tooSimilar = false;
      for (const asked of askedTopics) {
        const cw = nv.split(/\s+/);
        const aw = asked.split(/\s+/);
        if (cw.filter((w) => aw.includes(w)).length > cw.length * 0.6) {
          tooSimilar = true;
          break;
        }
      }
      if (!tooSimilar) filtered.push(chip);
    }
  }
  return filtered.slice(0, 3);
}

function resolveSafeSuggestedChips(
  candidateChips: SuggestedChip[] | undefined,
  approvedChips: SuggestedChip[],
): SuggestedChip[] {
  if (!candidateChips || candidateChips.length === 0) {
    return approvedChips;
  }

  const matched = matchSuggestedChipsToApprovedChips(
    candidateChips,
    approvedChips,
  );

  return matched.length > 0 ? matched : approvedChips;
}

export function ChatProvider({ children }: PropsWithChildren) {
  const location = useLocation();
  const adapterRef = useRef<ChatServiceAdapter>(getChatAdapter());
  const activeStreamRef = useRef<StreamingChatResponse | null>(null);
  const prevContextRef = useRef<PageContext | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const userRole = useAuthStore((s) => s.user?.role ?? "member");
  const pageContext = usePageContext();
  const { onboardingStage } = useOnboarding();
  const { data: meData } = useMe();
  // A14 dual-host: the user's effective host drives the command set Sprout emits.
  const preferredHost = resolvePreferredHost(meData?.preferredHost);

  const messages = useChatStore((s) => s.messages);
  const addMessage = useChatStore((s) => s.addMessage);
  const setThinking = useChatStore((s) => s.setThinking);
  const setStreaming = useChatStore((s) => s.setStreaming);
  const setPendingSuggestions = useChatStore((s) => s.setPendingSuggestions);
  const setNewSuggestion = useChatStore((s) => s.setNewSuggestion);
  const panelState = useChatStore((s) => s.panelState);
  const isQuietMode = useChatStore((s) => s.isQuietMode);
  const setNotification = useChatStore((s) => s.setNotification);

  // -----------------------------------------------------------------------
  // Build a full ContextPayload from the current page context + role filter
  // -----------------------------------------------------------------------
  const buildContextPayload = useCallback(() => {
    const filteredActions = filterActions(
      pageContext.availableActions,
      userRole,
    );
    const ctx: PageContext = { ...pageContext, availableActions: filteredActions };
    return toContextPayload(ctx, userRole, preferredHost);
  }, [pageContext, userRole, preferredHost]);

  // -----------------------------------------------------------------------
  // Context engine: route-change → filter → suggest → notify
  // -----------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;

    // Clear any pending debounce
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      // Skip when quiet mode is on
      if (isQuietMode) return;

      // Apply role-based action filtering
      const filteredActions = filterActions(
        pageContext.availableActions,
        userRole,
      );
      const filteredContext: PageContext = {
        ...pageContext,
        availableActions: filteredActions,
      };

      // Only regenerate suggestions on meaningful navigation
      if (!hasContextChanged(prevContextRef.current, filteredContext)) {
        return;
      }

      prevContextRef.current = filteredContext;

      const loadSuggestions = async () => {
        const approvedSuggestions = generateSuggestions(filteredContext, onboardingStage);
        const suggestionsFromAdapter =
          await adapterRef.current.getProactiveSuggestions(
            toContextPayload(filteredContext, userRole, preferredHost),
          );

        const suggestions = resolveSafeSuggestedChips(
          suggestionsFromAdapter,
          approvedSuggestions,
        );

        if (cancelled) return;

        const visibleSuggestions = filterVisibleSuggestedChips(suggestions);
        setPendingSuggestions(visibleSuggestions);

        if (panelState !== "open" && visibleSuggestions.length > 0) {
          setNewSuggestion(true);
        }
      };

      void loadSuggestions();
    }, CONTEXT_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [
    location.pathname,
    pageContext,
    userRole,
    preferredHost,
    onboardingStage,
    isQuietMode,
    panelState,
    setPendingSuggestions,
    setNewSuggestion,
  ]);

  // -----------------------------------------------------------------------
  // Flatten rich messages so the LLM can reference prior step/nav context
  // -----------------------------------------------------------------------
  const flattenForHistory = useCallback((msgs: ChatMessage[]): ChatMessage[] => {
    return msgs.map((m) => {
      // Steps: embed step text into content so LLM sees it
      if (m.type === "steps" && m.metadata?.type === "steps") {
        const steps = m.metadata.steps as Array<{ text: string }> | undefined;
        if (Array.isArray(steps) && steps.length > 0) {
          const stepTexts = steps
            .map((s, i) => `${String(i + 1)}. ${s.text ?? ""}`)
            .join("\n");
          return { ...m, content: `${m.content}\n${stepTexts}` };
        }
      }
      // Navigation: embed destination
      if (m.type === "navigation" && m.metadata?.type === "navigation") {
        const navMeta = m.metadata as { href?: string | undefined; text?: string | undefined };
        const href = navMeta.href ?? "";
        const dest =
          navMeta.text && href
            ? `${navMeta.text} (${href})`
            : href;
        return { ...m, content: `${m.content} [navigated to ${dest}]` };
      }
      return m;
    });
  }, []);

  const emitVerifiedDirectResponse = useCallback(
    (text: string): boolean => {
      const verifiedMessages = getVerifiedDirectResponse(text, location.pathname);
      if (!verifiedMessages || verifiedMessages.length === 0) {
        return false;
      }

      verifiedMessages.forEach((message) => addMessage(message));

      const allMsgs = [...useChatStore.getState().messages];
      setPendingSuggestions(filterVisibleSuggestedChips(generateFollowUpChips(allMsgs, location.pathname)));

      if (panelState !== "open" && !isQuietMode) {
        setNotification(true);
      }

      useChatStore.getState().setLastFailedMessage(null);
      return true;
    },
    [
      addMessage,
      isQuietMode,
      location.pathname,
      panelState,
      setNotification,
      setPendingSuggestions,
    ],
  );

  // -----------------------------------------------------------------------
  // sendMessage (non-streaming)
  // -----------------------------------------------------------------------
  const sendMessage = useCallback(
    async (text: string) => {
      // 1. Add user message to store
      const userMessage: ChatMessage = {
        id: nextUserMessageId(),
        role: "user",
        content: text,
        type: "text",
        timestamp: Date.now(),
      };
      addMessage(userMessage);
      playSound("send");

      if (emitVerifiedDirectResponse(text)) {
        return;
      }

      // 2. Show thinking state
      setThinking(true);

      try {
        // 2a. Extract grounding context from client-side knowledge base
        // (page-aware intents + onboarding patterns → injected as LLM context)
        const groundingContext = extractGroundingContext(
          text,
          location.pathname,
        );

        // 3. Build context from context engine
        const ctx = buildContextPayload();

        // 4. Call adapter — enrich message with grounding context if matched
        const messageForLLM = buildChatPrompt(text, groundingContext);

        const response = await adapterRef.current.sendMessage({
          message: messageForLLM,
          context: ctx,
          conversationHistory: flattenForHistory([...messages, userMessage]),
        });
        // 5. Add response to store
        addMessage(response.message);

        // 6. Update suggestions — prefer backend-provided, fall back to client-generated
        const allMsgs = [...useChatStore.getState().messages];
        const approvedFollowUps = generateFollowUpChips(allMsgs, location.pathname);
        const safeFollowUps = resolveSafeSuggestedChips(
          response.suggestedReplies,
          approvedFollowUps,
        );
        setPendingSuggestions(filterVisibleSuggestedChips(safeFollowUps));

        // 7. Notification if panel is not open
        if (panelState !== "open" && !isQuietMode) {
          setNotification(true);
        }

        // Clear any previous failed message on success
        useChatStore.getState().setLastFailedMessage(null);
      } catch (err: unknown) {
        // Categorise the error for a helpful user-facing message
        const errorContent = classifyError(err);
        addMessage({
          id: `error-${Date.now()}`,
          role: "assistant",
          content: errorContent,
          type: "text",
          timestamp: Date.now(),
        });
        useChatStore.getState().setLastFailedMessage(text);
      } finally {
        // 8. Reset thinking flag
        setThinking(false);
      }
    },
    [
      addMessage,
      setThinking,
      setPendingSuggestions,
      setNotification,
      messages,
      buildContextPayload,
      panelState,
      isQuietMode,
      location.pathname,
      emitVerifiedDirectResponse,
    ]
  );

  // -----------------------------------------------------------------------
  // sendMessageStreaming
  // -----------------------------------------------------------------------
  const sendMessageStreaming = useCallback(
    (text: string) => {
      // Abort any in-flight stream and clean up stale state
      if (activeStreamRef.current) {
        activeStreamRef.current.abort();
        activeStreamRef.current = null;
        setThinking(false);
        setStreaming(false);
        // Remove any leftover streaming placeholder
        const current = useChatStore.getState().messages;
        const cleaned = current.filter((m) => !m.id.startsWith("streaming-"));
        if (cleaned.length !== current.length) {
          useChatStore.setState({ messages: cleaned });
        }
      }

      // 1. Add user message
      const userMessage: ChatMessage = {
        id: nextUserMessageId(),
        role: "user",
        content: text,
        type: "text",
        timestamp: Date.now(),
      };
      addMessage(userMessage);
      playSound("send");

      if (emitVerifiedDirectResponse(text)) {
        return;
      }

      // 2. Thinking state
      setThinking(true);

      // 2a. Extract grounding context from client-side knowledge base
      const groundingContext = extractGroundingContext(text, location.pathname);

      // 3. Build context from context engine
      const ctx = buildContextPayload();

      // 4. Start streaming — enrich message with grounding context if matched
      const messageForLLM = buildChatPrompt(text, groundingContext);

      const streamResponse = adapterRef.current.sendMessageStreaming(
        {
          message: messageForLLM,
          context: ctx,
          conversationHistory: flattenForHistory([...messages, userMessage]),
        },
        {
          onToken: (token: string) => {
            // Streaming has begun — transition from thinking to streaming
            setThinking(false);
            setStreaming(true);

            // Update the placeholder assistant message with accumulated content.
            // We identify it by checking if the last message starts with "streaming-".
            const existing = useChatStore.getState().messages;
            const last = existing[existing.length - 1];
            if (last?.id.startsWith("streaming-")) {
              const updated = { ...last, content: last.content + token };
              useChatStore.setState({
                messages: [...existing.slice(0, -1), updated],
              });
            } else {
              // Insert new placeholder with a stable id per stream
              addMessage({
                id: `streaming-${userMessage.id}`,
                role: "assistant",
                content: token,
                type: "text",
                timestamp: Date.now(),
              });
            }
          },
          onComplete: (responseMessage: ChatMessage) => {
            // Replace streaming placeholder with final message
            const existing = useChatStore.getState().messages;
            const withoutPlaceholder = existing.filter(
              (m) => !m.id.startsWith("streaming-")
            );
            useChatStore.setState({
              messages: [...withoutPlaceholder, responseMessage],
            });
            setStreaming(false);
            activeStreamRef.current = null;

            // Clear any previous failed message on success
            useChatStore.getState().setLastFailedMessage(null);

            // Surface co-located suggestion chips — keep only suggestions that
            // match the approved frontend follow-up set for this page/context.
            const allMsgs = [...useChatStore.getState().messages];
            const approvedFollowUps = generateFollowUpChips(
              allMsgs,
              location.pathname,
            );
            const safeFollowUps = resolveSafeSuggestedChips(
              responseMessage.suggestedReplies,
              approvedFollowUps,
            );
            setPendingSuggestions(filterVisibleSuggestedChips(safeFollowUps));

            // Notification if panel is closed/minimized
            if (panelState !== "open" && !isQuietMode) {
              setNotification(true);
            }
          },
          onError: (err: unknown) => {
            setThinking(false);
            setStreaming(false);
            // Remove any streaming placeholder message so partial content isn't stuck
            const current = useChatStore.getState().messages;
            const cleaned = current.filter((m) => !m.id.startsWith("streaming-"));
            const updated = [
              ...cleaned,
              {
                id: `error-${Date.now()}`,
                role: "assistant" as const,
                content: classifyError(err),
                type: "text" as const,
                timestamp: Date.now(),
              },
            ];
            useChatStore.setState({
              messages: updated.length > MAX_MESSAGES ? updated.slice(-MAX_MESSAGES) : updated,
            });
            useChatStore.getState().setLastFailedMessage(text);
            activeStreamRef.current = null;
          },
        }
      );

      activeStreamRef.current = streamResponse;
    },
    [
      addMessage,
      setThinking,
      setStreaming,
      setNotification,
      messages,
      buildContextPayload,
      panelState,
      isQuietMode,
      location.pathname,
      emitVerifiedDirectResponse,
    ]
  );

  // -----------------------------------------------------------------------
  // Cleanup active stream on unmount
  // -----------------------------------------------------------------------
  useEffect(() => {
    return () => {
      activeStreamRef.current?.abort();
    };
  }, []);

  // -----------------------------------------------------------------------
  // Retry last failed message
  // -----------------------------------------------------------------------
  const retryLastMessage = useCallback(() => {
    const failed = useChatStore.getState().lastFailedMessage;
    if (!failed) return;
    // Remove the error message before retrying
    const msgs = useChatStore.getState().messages;
    const cleaned = msgs.filter((m) => !m.id.startsWith("error-"));
    useChatStore.setState({ messages: cleaned, lastFailedMessage: null });
    void sendMessage(failed);
  }, [sendMessage]);

  // -----------------------------------------------------------------------
  // Stop active streaming
  // -----------------------------------------------------------------------
  const stopStreaming = useCallback(() => {
    if (!activeStreamRef.current) return;
    activeStreamRef.current.abort();
    activeStreamRef.current = null;
    setThinking(false);
    setStreaming(false);
    // Keep whatever partial content was streamed so far
    const msgs = useChatStore.getState().messages;
    const last = msgs[msgs.length - 1];
    if (last?.id.startsWith("streaming-")) {
      // Convert placeholder into a real message
      const final: ChatMessage = {
        ...last,
        id: `msg-${Date.now()}`,
        content: last.content || "Response interrupted.",
      };
      useChatStore.setState({ messages: [...msgs.slice(0, -1), final] });
    }
  }, [setThinking, setStreaming]);

  // -----------------------------------------------------------------------
  // -----------------------------------------------------------------------
  // Listen for navigation events — route through LLM with page context
  // -----------------------------------------------------------------------
  useEffect(() => {
    let navTimer: ReturnType<typeof setTimeout> | null = null;
    let lastNavHref: string | null = null;
    let aborted = false;

    const handleNavigated = (e: Event) => {
      const detail = (e as CustomEvent<{ href?: string; text?: string }>).detail;
      if (typeof detail.href !== "string") return;
      const rawHref = detail.href;

      const href = normalizeChatRoute(rawHref);

      // Dedup: ignore same navigation within 2s (StrictMode double-mount)
      if (href === lastNavHref) return;
      lastNavHref = href;
      setTimeout(() => {
        lastNavHref = null;
      }, 2000);

      const pageKnowledge = getPageKnowledge(href);
      if (!pageKnowledge) return;

      const label =
        href
          .replace(/^\//, "")
          .replace(/-/g, " ")
          .replace(/\b\w/g, (c) => c.toUpperCase()) || "Dashboard";

      if (navTimer) clearTimeout(navTimer);
      navTimer = setTimeout(() => {
        navTimer = null;
        if (aborted) return;

        // Send through the LLM — no visible user message
        const messageForLLM = [
          `[Relevant SpecTree context for this page]`,
          pageKnowledge,
          `[End context]`,
          ``,
          CHAT_RESPONSE_STYLE_RULES,
          `- Respond with 2-4 short numbered points.`,
          `- Keep each point to one short sentence.`,
          `- This is a page tour, so focus on what the user can do on this page.`,
          ``,
          `The user just navigated to the ${label} page. Briefly greet them and explain what they can do here.`,
        ].join("\n");

        setThinking(true);

        const currentMessages = useChatStore.getState().messages;
        const ctx = buildContextPayload();

        adapterRef.current
          .sendMessage({
            message: messageForLLM,
            context: ctx,
            conversationHistory: flattenForHistory(currentMessages),
          })
          .then((response) => {
            if (aborted) return;

            // Post-process: add tour targets to LLM-generated steps
            const enhanced = enhanceMessageWithTours(response.message, href);
            addMessage(enhanced);

            // Generate follow-up chips
            const allMsgs = useChatStore.getState().messages;
            setPendingSuggestions(filterVisibleSuggestedChips(generateFollowUpChips(allMsgs, href)));
          })
          .catch(() => {
            // Navigation guidance is non-critical — fail silently
          })
          .finally(() => {
            if (!aborted) setThinking(false);
          });
      }, 600);
    };

    window.addEventListener("spectree:navigated", handleNavigated);
    return () => {
      aborted = true;
      window.removeEventListener("spectree:navigated", handleNavigated);
      if (navTimer) clearTimeout(navTimer);
    };
  }, [addMessage, setThinking, buildContextPayload, flattenForHistory, setPendingSuggestions]);

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  return (
    <ChatServiceContext.Provider value={{ sendMessage, sendMessageStreaming, retryLastMessage, stopStreaming }}>
      {children}
    </ChatServiceContext.Provider>
  );
}
